/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injector} from '@angular/core';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';

import {OBJECT_TYPE, ObjectTypeFinder} from '../common/constants/object-type';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {getResourceItemTitleResolves, getResourceItemTitleView} from '../base/default-item-title-view';
import {Uuid} from '../../components/util/uuid';
import {ModelComms} from '../common/controller/model-comms';
import {AdnRouterHelperApi} from './state-utils';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {AngularViewRefreshService} from '../common/util/angular-view-refresh.service';
import {FeatureStateDependencies} from './feature-state-dependencies';

import {ResourceListRoutePageComponent} from '../common/controller/resource-list-route-page.component';
import {listPageRouteResolve} from '../common/controller/admin-resource-routing';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';

function listTemplateToRegistryKey(listTemplate: string | undefined | null): string | null {
  if (!listTemplate) {
    return null;
  }
  const match = listTemplate.match(/<adn-([\w-]+)-list-page/);
  return match ? `${match[1]}-list` : null;
}

export function resolveListReadOnly(
  readOnly: boolean,
  sectionParam: string,
  LocalUserPermissions: any
): boolean {
  const sectionPerms = LocalUserPermissions.getSectionPerms();
  const specificEditableParameter = sectionParam + 'Editable';
  if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, specificEditableParameter)) {
    return !sectionPerms[specificEditableParameter];
  }
  if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, sectionParam)) {
    return !sectionPerms[sectionParam];
  }
  return !!readOnly;
}

export function createAdnRouterHelper(ng2Injector: Injector): AdnRouterHelperApi {
  function createModelComms(): ModelComms {
    return new ModelComms(() => ng2Injector.get(AngularViewRefreshService).schedule());
  }

  function getResource(resourceProvider: any): any {
    return ng2Injector.get(resourceProvider);
  }

  function RouterHelper(
    this: any,
    states: string,
    resourceProvider: any,
    childResourceProvider: any | null,
    parent: string,
    _dir: string,
    listTemplate: string
  ) {
    const rh = this;
    const splitStates = states.split('.');
    const statePlural = splitStates[0];
    const state = splitStates[1];

    rh.listPath = function() {
      return parent + '.' + statePlural;
    };
    rh.list = function(extras: any) {
      const listPageKey = listTemplateToRegistryKey(listTemplate);
      const listRouteResolve = listPageKey ? listPageRouteResolve(listPageKey) : {};
      const views = {
        'content@app': _.assign({
          component: ResourceListRoutePageComponent,
        }, listPageKey ? {bindings: {listPage: 'listPageRouteKey'}} : {}),
        'context@app': {
          component: ListDefaultActionsComponent,
          bindings: {
            readOnly: 'listReadOnly',
            newStateName: 'listNewStateName'
          }
        },
        'title@app': stateTitleView()
      };
      return {
        url: '/' + statePlural,
        resolve: _.merge({}, {
          ResourceFactory: function() {
            return getResource(resourceProvider);
          },
          ChildResourceFactory: function() {
            return childResourceProvider ? getResource(childResourceProvider) : null;
          },
          orderByParam: function() {
            return 'name';
          },
          listConfig: function() {
          },
          objectType: function() {
            return ObjectTypeFinder.findObjectFromStateName(states);
          },
          sectionParam: function() {
          },
          extension: function() {
          },
          listExtension: function() {
          },
          bulk: function() {
          },
          listReadOnly: /*@ngInject*/ function(readOnly: boolean, sectionParam: string) {
            return resolveListReadOnly(readOnly, sectionParam, ng2Injector.get(LocalUserPermissions));
          },
          listNewStateName: function() {
            return rh.newItemPath();
          }
        }, listRouteResolve, _.get(extras, 'resolve', {})),
        views: _.assign({}, views, _.get(extras, 'views', {})),
        data: _.assign({}, _.get(extras, 'data', {}), {
          title: _.camelCase(state) + ".plural"
        })
      };
    };

    rh.newPath = function(pathState: string) {
      return parent + '.' + statePlural + '.' + pathState;
    };

    rh.newItemPath = function() {
      return parent + '.' + statePlural + '.new';
    };
    rh.newItem = function(extras: any) {
      return {
        url: '/new',
        params: _.merge({}, {
          copyId: false, folioId: false, folioName: false, orderId: false, orderName: false, full: false, viewType: false,
          advertiserId: false, advertiserName: false, earningsAccountId: false, earningsAccountName: false,
          earningsAccountCountry: false, earningsAccountOwnerTeamId: false
        }, extras || {}),
        views: {},
        onEnter: function($transition$: any) {
          const $stateParams = $transition$.params('to');
          const passExtras: any = {};
          if (extras) {
            _.forEach(extras, function(v, k) {
              passExtras[k] = $stateParams[k];
            });
          }
          if ($stateParams.copyId) {
            passExtras.copyId = $stateParams.copyId;
          }
          if ($stateParams.orderId && $stateParams.orderName) {
            passExtras.orderId = $stateParams.orderId;
            passExtras.orderName = $stateParams.orderName;
          }
          if ($stateParams.advertiserId && $stateParams.advertiserName) {
            passExtras.advertiserId = $stateParams.advertiserId;
            passExtras.advertiserName = $stateParams.advertiserName;
          }
          if ($stateParams.folioId && $stateParams.folioName) {
            passExtras.folioId = $stateParams.folioId;
            passExtras.folioName = $stateParams.folioName;
          }
          if ($stateParams.earningsAccountId && $stateParams.earningsAccountName) {
            passExtras.earningsAccountId = $stateParams.earningsAccountId;
            passExtras.earningsAccountName = $stateParams.earningsAccountName;
            passExtras.earningsAccountCountry = $stateParams.earningsAccountCountry;
            passExtras.earningsAccountOwnerTeamId = $stateParams.earningsAccountOwnerTeamId;
          }
          if ($stateParams.viewType) {
            passExtras.viewType = $stateParams.viewType;
          }
          if ($stateParams.full) {
            passExtras.full = $stateParams.full;
          }
          return ng2Injector.get(StateService).target(
            parent + '.' + states,
            _.merge({}, {id: Uuid.generate(), isNew: true}, passExtras || {})
          );
        }
      };
    };

    rh.itemPath = function() {
      return parent + '.' + states;
    };

    rh.modelCommsItem = function(aExtras: any, aObjectType: string, aCrumbParams: any) {
      _.set(aExtras, 'resolve.modelComms', function() {
        return createModelComms();
      });

      if (aObjectType && OBJECT_TYPE[aObjectType]) {
        _.set(aExtras, 'views.title@app', getResourceItemTitleView());
        aExtras.resolve = _.merge({}, getResourceItemTitleResolves(aObjectType, aCrumbParams), aExtras.resolve || {});
      }

      return rh.item(aExtras, aObjectType);
    };
    rh.item = function(aExtras: any) {
      const views = {
        'content@app': {},
        'context@app': {},
        'title@app': {}
      };
      return {
        url: _.get(aExtras, 'url') || '/' + state + '/:id',
        params: _.merge({}, {
          isNew: false, full: false, viewType: false, copyId: false, folioId: false, folioName: false, orderId: false,
          orderName: false, advertiserId: false, advertiserName: false, earningsAccountId: false, earningsAccountName: false,
          earningsAccountCountry: false, earningsAccountOwnerTeamId: false
        }, _.get(aExtras, 'params', {})),
        resolve: _.merge({}, {
          isNew: /*@ngInject*/ function($stateParams: any) {
            return !!$stateParams.isNew;
          },
          model: /*@ngInject*/ function($stateParams: any) {
            const resource = getResource(resourceProvider);
            if ($stateParams.isNew) {
              return _.isFunction(resource.create) ? resource.create({id: $stateParams.id}) : new resource({id: $stateParams.id});
            }
            return resource.get({id: $stateParams.id}).$promise;
          }
        }, _.get(aExtras, 'resolve', {})),
        views: _.assign({}, views, _.get(aExtras, 'views')),
        data: _.assign({}, _.get(aExtras, 'data', {}), {
          title: _.get(aExtras, ['data', 'title'], _.camelCase(state))
        })
      };
    };

    rh.generateStates = function($stateProvider: any) {
      $stateProvider.state(rh.listPath(), rh.list())
        .state(rh.newItemPath(), rh.newItem())
        .state(rh.itemPath(), rh.item());
    };

    rh.getResourceItemTitleView = function() {
      return getResourceItemTitleView();
    };
    rh.getDefaultTitleView = function() {
      return getResourceItemTitleView();
    };
  }

  return {
    services: new FeatureStateDependencies(ng2Injector),
    createModelComms,
    createRouterHelper(states, resourceProvider, parent, dir, listTemplate) {
      return new (RouterHelper as any)(states, resourceProvider, null, parent, dir, listTemplate);
    },
    createRouterAndChildHelper(states, resourceProvider, childProvider, parent, dir, listTemplate) {
      return new (RouterHelper as any)(states, resourceProvider, childProvider, parent, dir, listTemplate);
    }
  };
}
